import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertTriangle, Download, Maximize2, Minimize2, Volume2, VolumeX, Play, Pause, SkipBack, SkipForward, Loader2, Share2, Clock, Hash, Gauge, Compass, MapPin, Mountain, Satellite, Calendar, FileVideo, Timer, Cpu, Truck } from 'lucide-react';
import { formatDT, formatTimeOnly, formatWallDT, formatWallTimeOnly, resolveTimezone, formatSpeed } from '../../utils/timezone';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { toast } from 'sonner';
import { EventTrailMap } from './EventTrailMap';
import { ShareEventEvidenceDialog } from './ShareEventEvidenceDialog';
import { ForensicTelemetrySnapshotDialog } from './ForensicTelemetrySnapshotDialog';
import { deviceDisplayName } from '../../utils/deviceName';
import { getHevcSupportSnapshot, hasRenderedVideoFrames, isLikelyHevcVideo } from '../../utils/videoCompatibility';

export const VideoPlayerModal = ({ open, onClose, video, videoUrl, device = null }) => {
  const { api } = useAuth();
  const { isFeatureEnabled } = useFeatureFlags();
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [forensicOpen, setForensicOpen] = useState(false);
  const [activeVideoUrl, setActiveVideoUrl] = useState(videoUrl);
  const [hevcSupport, setHevcSupport] = useState(null);
  const [compatibilityIssue, setCompatibilityIssue] = useState(null);
  const [compatibilityLoading, setCompatibilityLoading] = useState(false);
  const [compatibilityStatus, setCompatibilityStatus] = useState(null);
  const [compatibilityProgress, setCompatibilityProgress] = useState(null);
  const containerRef = useRef(null);
  const compatibilityPollRef = useRef(null);

  // Privilege gate for the Share Evidence Link button. Strict for all
  // roles (no super-admin bypass) so admins can verify the gate by
  // turning the flag off on themselves.
  const canShareEvidence = isFeatureEnabled('media_share_event_evidence');
  // Forensic Telemetry Snapshot reuses the existing History Forensic
  // Panel privilege — no new flag. Visible only on event-coded clips.
  const canViewForensic = isFeatureEnabled('analytics_history_forensic_panel');
  const hasEventCode = video?.event_type && String(video.event_type) !== '0' && String(video.event_type) !== 'remote_download';

  // Reset + wire the <video> element whenever the modal is (re-)opened
  // or the source URL changes.  Without this reset every state variable
  // (isPlaying, isFullscreen, currentTime, duration…) carried over from
  // the previous modal session, leading to de-synced icons.

  useEffect(() => {
    setActiveVideoUrl(videoUrl || null);
    setCompatibilityIssue(null);
    setCompatibilityLoading(false);
    setCompatibilityStatus(null);
    setCompatibilityProgress(null);
    if (compatibilityPollRef.current) {
      clearTimeout(compatibilityPollRef.current);
      compatibilityPollRef.current = null;
    }
    if (!open) {
      // Modal is closing — fully tear down so the NEXT open starts clean.
      if (videoRef.current) {
        try { videoRef.current.pause(); } catch { /* noop */ }
        videoRef.current.removeAttribute('src');
        try { videoRef.current.load(); } catch { /* noop */ }
      }
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setIsFullscreen(Boolean(document.fullscreenElement));
      return;
    }
    // Modal just opened — reset transient state.
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsFullscreen(Boolean(document.fullscreenElement));
    if (videoRef.current && videoUrl) {
      videoRef.current.src = videoUrl;
      videoRef.current.load();
    }
  }, [open, videoUrl]);


  useEffect(() => {
    if (!open || !videoRef.current || !activeVideoUrl) return;
    if (videoRef.current.src !== activeVideoUrl) {
      videoRef.current.src = activeVideoUrl;
      videoRef.current.load();
    }
  }, [open, activeVideoUrl]);

  useEffect(() => {
    let cancelled = false;
    if (!open) return undefined;
    getHevcSupportSnapshot().then(snapshot => {
      if (!cancelled) setHevcSupport(snapshot);
    });
    return () => { cancelled = true; };
  }, [open]);

  const compatibleEndpoint = video?.video_id
    ? `/media/event-videos/${video.video_id}/compatible-version`
    : video?.download_id
      ? `/media/remote/download/${video.download_id}/compatible-version`
      : null;
  const isCompatibilityBusy = compatibilityLoading || ['queued', 'starting', 'downloading', 'video', 'audio', 'uploading'].includes(compatibilityProgress?.phase);

  const pollCompatibleVersion = async (method = 'get') => {
    if (!compatibleEndpoint) {
      setCompatibilityIssue({
        title: 'Compatible playback is unavailable',
        message: 'This clip cannot be converted from this view.',
      });
      return;
    }
    try {
      setCompatibilityLoading(true);
      const res = method === 'post' ? await api.post(compatibleEndpoint) : await api.get(compatibleEndpoint);
      if (res.data?.url) {
        setActiveVideoUrl(res.data.url);
        setCompatibilityIssue(null);
        setCompatibilityStatus('Compatible H.264 version ready');
        setCompatibilityProgress(res.data?.h264_fallback_progress || { phase: 'ready', percent: 100, estimated_seconds_remaining: 0 });
        toast.success('Compatible version ready');
        return;
      }
      const status = res.data?.fallback_status || res.data?.status || 'queued';
      const message = res.data?.message || 'Preparing compatible H.264 version';
      setCompatibilityStatus(message);
      setCompatibilityProgress(res.data?.h264_fallback_progress || null);
      if (['queued', 'converting', 'waiting_for_playback_derivative'].includes(status)) {
        const retryMs = Math.max(3, Number(res.data?.retry_after || 5)) * 1000;
        compatibilityPollRef.current = setTimeout(() => pollCompatibleVersion('get'), retryMs);
        return;
      }
      throw new Error(message || 'Compatible version is not available');
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to prepare compatible video';
      setCompatibilityIssue({
        title: 'Compatible playback failed',
        message: typeof detail === 'string' ? detail : JSON.stringify(detail),
      });
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setCompatibilityLoading(false);
    }
  };

  const startCompatiblePlayback = () => {
    setCompatibilityIssue(null);
    setCompatibilityStatus('Preparing compatible H.264 version');
    pollCompatibleVersion('post');
  };

  useEffect(() => () => {
    if (compatibilityPollRef.current) clearTimeout(compatibilityPollRef.current);
  }, []);


  useEffect(() => {
    if (!open || !activeVideoUrl || !isLikelyHevcVideo(video) || hevcSupport?.supported !== false || activeVideoUrl !== videoUrl) return;
    setCompatibilityIssue({
      title: 'This laptop may not support H.265 video',
      message: 'Create a compatible H.264 version for this device.',
    });
  }, [open, activeVideoUrl, video, videoUrl, hevcSupport]);


  // Keep the fullscreen icon in sync with the real fullscreen state —
  // ESC exits fullscreen without going through our toggle, so without
  // this listener the Maximize/Minimize icon would show the wrong
  // state until the user clicked twice.
  useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
    };
  }, []);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused || el.ended) {
      // Don't optimistically flip isPlaying — let the native onPlay
      // event drive the state.  If play() rejects (autoplay policy,
      // buffering stall, etc.) the UI stays in the correct state
      // instead of lying to the user with a Pause icon over a frozen
      // video at 0:00.
      const p = el.play();
      if (p && typeof p.then === 'function') {
        p.catch(() => setIsPlaying(false));
      }
    } else {
      el.pause();
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    // Decide by the real state, not the mirrored React state, so we
    // can't get it wrong even if a previous sync was missed.
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    // isFullscreen will be updated by the fullscreenchange listener —
    // do NOT set it here (that was the source of the desync).
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      if (hasRenderedVideoFrames(videoRef.current)) setCompatibilityIssue(null);
    }
  };

  const detectPlaybackIssue = useCallback(() => {
    const el = videoRef.current;
    if (!el || !activeVideoUrl || activeVideoUrl !== videoUrl) return;
    const progressed = Number(el.currentTime || 0) > 2;
    if (progressed && !hasRenderedVideoFrames(el)) {
      setCompatibilityIssue({
        title: 'Video frames are not rendering on this device',
        message: 'Audio can play while the laptop cannot decode the video stream. Create a compatible H.264 version.',
      });
    }
  }, [activeVideoUrl, videoUrl]);

  const handleVideoError = () => {
    if (activeVideoUrl && activeVideoUrl === videoUrl) {
      setCompatibilityIssue({
        title: 'This browser cannot decode the video',
        message: 'Create a compatible H.264 version for this device.',
      });
    }
    setIsPlaying(false);
  };

  useEffect(() => {
    if (!open || !activeVideoUrl || !isPlaying || compatibilityIssue || activeVideoUrl !== videoUrl) return undefined;
    const id = setInterval(detectPlaybackIssue, 2000);
    return () => clearInterval(id);
  }, [open, activeVideoUrl, videoUrl, isPlaying, compatibilityIssue, detectPlaybackIssue]);

  const handleSeek = (e) => {
    if (!videoRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pct * duration;
  };

  /**
   * Trigger a real file download (not a new tab with autoplay).
   *
   * We request a *fresh* presigned URL that carries
   * ``ResponseContentDisposition: attachment; filename=<original>`` so
   * S3 returns the stream with the correct filename header, then fetch
   * it as a blob and synthesize an <a download> click.  Falls back to
   * the inline ``videoUrl`` prop if we don't have a download_id /
   * video_id to request a fresh URL from.
   */
  const handleDownload = async () => {
    if (!video) return;
    setDownloading(true);
    try {
      const forcedName = video.filename || `${video.event_label || 'video'}.mp4`;
      let url = null;
      try {
        if (video.download_id) {
          const res = await api.get(`/media/remote/download/${video.download_id}/play-url`, {
            params: { disposition: 'attachment', filename: forcedName },
          });
          url = res.data?.url;
        } else if (video.video_id) {
          const res = await api.get(`/media/event-videos/${video.video_id}/play-url`, {
            params: { disposition: 'attachment', filename: forcedName },
          });
          url = res.data?.url;
        } else {
          url = videoUrl;
        }
      } catch (apiErr) {
        const status = apiErr?.response?.status;
        const detail = apiErr?.response?.data?.detail;
        toast.error(detail
          ? `Cannot prepare download${status ? ` (${status})` : ''}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`
          : `Failed to prepare download${status ? ` (${status})` : ''}`);
        return;
      }
      if (!url) {
        toast.error('No download URL available');
        return;
      }

      const resp = await fetch(url);
      if (!resp.ok) {
        throw new Error(`S3 returned HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = forcedName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoke after a tick so Safari doesn't cancel the save.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
      toast.success('Download started');
    } catch (e) {

      console.error('[VideoPlayerModal] download failed:', e);
      toast.error(e?.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const skip = (seconds) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + seconds));
  };

  const formatTime = (s) => {
    if (!s || isNaN(s)) return '0:00';
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (!video) return null;

  const channelName = video.channel_label || video.channel_type_name || (video.channel != null ? `CH${video.channel}` : 'Channel');
  const channelNumber = video.channel != null ? `CH${video.channel}` : null;
  const isRemoteDownload = video.event_type === 'remote_download';
  const overlayStart = video.event_time || video.start_time || video.created_at;
  const overlayEnd = video.end_time;
  const overlayTime = overlayStart
    ? `${video.event_time_is_wall_clock ? formatWallDT(overlayStart, { seconds: true }) : formatDT(overlayStart, { device, seconds: true })}${overlayEnd ? ` → ${video.event_time_is_wall_clock ? formatWallTimeOnly(overlayEnd, { seconds: true }) : formatTimeOnly(overlayEnd, { device, seconds: true })}` : ''}`
    : null;
  const overlayTitle = video.event_label_display || video.event_label || video.event_type || 'Video';
  const eventMetaLabel = video.has_multiple_events ? 'Events' : 'Event';
  const eventCodeBadge = Array.isArray(video.event_codes) && video.event_codes.length > 0
    ? video.event_codes.join(', ')
    : (video.event_type || 'Video');
  const displayDeviceName = (device ? deviceDisplayName(device) : null)
    || video.device_name
    || 'Unknown Vehicle';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl p-0 gap-0 overflow-hidden bg-black max-h-[95vh] overflow-y-auto [&>button.absolute]:bg-white/15 [&>button.absolute]:text-white [&>button.absolute]:border-white/25 [&>button.absolute:hover]:bg-white/35 [&>button.absolute:hover]:border-white/50"
        data-testid="video-player-modal"
        // Explicitly ignore backdrop clicks so an accidental click
        // outside the player doesn't dismiss what you're watching.
        // The X (top-right) and ESC still work to close.
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Accessible (visually hidden) title — Radix Dialog requires a
            DialogTitle inside every DialogContent for screen readers.
            The visible title is rendered in the gradient header below. */}
        <DialogTitle className="sr-only">
          {overlayTitle}
        </DialogTitle>
        <div ref={containerRef} className="relative">
          {/* Header — pointer-events-none on the gradient wrapper so the
              shadcn built-in close [X] at top-right remains clickable.
              We re-enable pointer events on the inline text + badges so
              you can still select / copy them if desired.  pr-12 leaves
              visual breathing room for the X icon.  No custom X button:
              the shadcn one is the single source of truth, which
              resolves the duplicate-[X] issue. */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4 pr-12 pointer-events-none select-text">
            <div className="pointer-events-auto">
              <h3 className="text-white font-semibold text-lg truncate" data-testid="video-player-overlay-title">{overlayTitle}</h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge className={isRemoteDownload ? 'bg-emerald-600 text-white text-xs' : 'bg-blue-600 text-white text-xs'} data-testid="video-player-overlay-channel-name">
                  {channelName}
                </Badge>
                {channelNumber && channelName !== channelNumber && (
                  <Badge variant="outline" className="text-white/70 border-white/30 text-[10px]" data-testid="video-player-overlay-channel-number">
                    {channelNumber}
                  </Badge>
                )}
                <Badge className="bg-slate-600 text-white text-xs" data-testid="video-player-overlay-mode-badge">
                  {isRemoteDownload ? 'Playback' : eventCodeBadge}
                </Badge>
                {video.has_multiple_events && (
                  <Badge className="bg-amber-600 text-white text-xs" data-testid="video-player-overlay-multiple-events-badge">
                    Multiple Events
                  </Badge>
                )}
                {displayDeviceName && (
                  <span className="text-white/70 text-sm truncate" data-testid="video-player-overlay-device-name">{displayDeviceName}</span>
                )}
                {overlayTime && (
                  <span
                    className="text-white/60 text-xs truncate"
                    title={resolveTimezone({ device }) ? `Timezone: ${resolveTimezone({ device })}` : ''}
                    data-testid="video-player-overlay-time"
                  >
                    {overlayTime}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Video Element */}
          <div className="aspect-video bg-black flex items-center justify-center relative">
            {activeVideoUrl ? (
              <video
                ref={videoRef}
                className="w-full h-full object-contain"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={() => setIsPlaying(false)}
                onError={handleVideoError}
                data-testid="video-element"
              />
            ) : (
              <div className="text-white/60 text-center" data-testid="video-player-pending-state">
                {['queued', 'converting'].includes(video.playback_status) ? (
                  <Loader2 className="w-16 h-16 mx-auto mb-3 animate-spin opacity-70" />
                ) : (
                  <Play className="w-16 h-16 mx-auto mb-2 opacity-30" />
                )}
                <p className="text-sm" data-testid="video-player-pending-title">
                  {video.playback_status === 'queued' ? 'Video queued for conversion' : video.playback_status === 'converting' ? 'Converting video for playback' : 'Video not yet available'}
                </p>
                <p className="text-xs mt-1 text-white/35" data-testid="video-player-pending-message">
                  {compatibilityStatus || video.playback_message || 'File may still be uploading from device'}
                </p>
              </div>
            )}
            {compatibilityIssue && activeVideoUrl && (
              <div className="absolute inset-0 bg-black/82 backdrop-blur-sm flex items-center justify-center p-6" data-testid="video-compatibility-warning">
                <div className="max-w-md text-center text-white">
                  <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-300" />
                  <h4 className="font-semibold text-lg" data-testid="video-compatibility-warning-title">{compatibilityIssue.title}</h4>
                  <p className="text-sm text-white/70 mt-2" data-testid="video-compatibility-warning-message">{compatibilityIssue.message}</p>
                  {compatibilityStatus && (
                    <p className="text-xs text-blue-200 mt-3" data-testid="video-compatibility-status">{compatibilityStatus}</p>
                  )}
                  <CompatibilityProgress progress={compatibilityProgress} status={compatibilityStatus} />
                  {!isCompatibilityBusy && (
                    <Button
                      onClick={startCompatiblePlayback}
                      disabled={!compatibleEndpoint}
                      className="mt-4 bg-amber-500 hover:bg-amber-400 text-black"
                      data-testid="video-create-compatible-version-button"
                    >
                      <FileVideo className="w-4 h-4 mr-2" />
                      Create compatible version
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Controls */}
          {activeVideoUrl && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
              {/* Progress bar */}
              <div
                className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer mb-3 group"
                onClick={handleSeek}
                data-testid="video-progress-bar"
              >
                <div
                  className="h-full bg-blue-500 rounded-full relative group-hover:bg-blue-400 transition-colors"
                  style={{ width: duration ? `${(currentTime / duration) * 100}%` : '0%' }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
              {/* Buttons */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-8 w-8" onClick={() => skip(-10)} data-testid="video-skip-back-button">
                    <SkipBack className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-10 w-10" onClick={togglePlay} data-testid="video-play-btn">
                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                  </Button>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-8 w-8" onClick={() => skip(10)} data-testid="video-skip-forward-button">
                    <SkipForward className="w-4 h-4" />
                  </Button>
                  <span className="text-white/70 text-sm ml-2" data-testid="video-playback-time">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-8 w-8" onClick={toggleMute} data-testid="video-mute-button">
                    {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                  </Button>
                  <Button variant="ghost" size="icon" className="text-white hover:bg-white/20 h-8 w-8" onClick={toggleFullscreen} data-testid="video-fullscreen-button">
                    {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </Button>
                  {activeVideoUrl && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/20 h-8 w-8"
                      onClick={handleDownload}
                      disabled={downloading}
                      title="Download original file"
                      data-testid="video-download-btn"
                    >
                      {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    </Button>
                  )}
                  {canViewForensic && hasEventCode && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/20 h-8 w-8"
                      onClick={() => setForensicOpen(true)}
                      title="Forensic Telemetry Snapshot — frame-accurate GPS at the moment of the event"
                      data-testid="video-forensic-snapshot-btn"
                    >
                      <Cpu className="w-4 h-4" />
                    </Button>
                  )}
                  {activeVideoUrl && canShareEvidence && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/20 h-8 w-8"
                      onClick={() => setShareOpen(true)}
                      title="Share evidence link (insurance / compliance)"
                      data-testid="video-share-evidence-btn"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Video Info Footer — icon-grid styling matches the public
            /evidence/<token> page for visual parity. Same MetaItem
            component shape; coords link to Google Maps in a new tab. */}
        <div className="bg-slate-900 px-4 py-3 border-t border-slate-800">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <MetaItem
              icon={Truck}
              label="Vehicle"
              value={displayDeviceName}
              testid="video-info-device-name"
            />
            {!isRemoteDownload && overlayStart && (
              <MetaItem
                icon={Clock}
                label="Time"
                value={video.event_time_is_wall_clock ? formatWallDT(overlayStart, { seconds: true }) : formatDT(overlayStart, { device, seconds: true })}
                testid="video-info-event-time"
              />
            )}
            {isRemoteDownload && overlayTime && (
              <MetaItem
                icon={Clock}
                label="Time"
                value={overlayTime}
                testid="video-info-event-time-range"
              />
            )}
            {(video.event_label_display || video.event_label || video.event_type) && (
              <MetaItem icon={Hash} label={eventMetaLabel} value={video.event_label_display || video.event_label || video.event_type} testid="video-info-event-label" />
            )}
            {video.speed != null && (
              <MetaItem icon={Gauge} label="Speed" value={formatSpeed(video.speed)} testid="video-info-speed" />
            )}
            {video.heading != null && (
              <MetaItem icon={Compass} label="Heading" value={`${Math.round(video.heading)}°`} testid="video-info-heading" />
            )}
            {video.latitude != null && video.longitude != null && (
              <MetaItem
                icon={MapPin}
                label="Location"
                value={`${Number(video.latitude).toFixed(5)}, ${Number(video.longitude).toFixed(5)}`}
                link={`https://www.google.com/maps?q=${Number(video.latitude)},${Number(video.longitude)}`}
                title={`Open in Google Maps — ${Number(video.latitude).toFixed(6)}, ${Number(video.longitude).toFixed(6)}`}
                testid="video-info-location"
              />
            )}
            {video.altitude != null && (
              <MetaItem icon={Mountain} label="Altitude" value={`${video.altitude} m`} testid="video-info-altitude" />
            )}
            {video.satellites != null && (
              <MetaItem icon={Satellite} label="Satellites" value={video.satellites} testid="video-info-satellites" />
            )}
            {(video.channel_name || video.channel != null) && (
              <MetaItem icon={Calendar} label="Channel" value={video.channel_name || `Ch ${video.channel}`} testid="video-info-channel" />
            )}
            {video.duration_seconds && (
              <MetaItem icon={Timer} label="Duration" value={`${video.duration_seconds}s`} testid="video-info-duration" />
            )}
            {video.file_size_display && (
              <MetaItem icon={FileVideo} label="Size" value={video.file_size_display} testid="video-info-size" />
            )}
          </div>
        </div>

        {/* Trail Map (collapsible) — drops down inside the modal so the user
            never leaves this view. Renders only when the event has GPS coords;
            reads the user's current Live Tracking map type + dark mode and
            queries the existing tenant-isolated /api/gps/history endpoint. */}
        <EventTrailMap video={video} device={device} currentVideoTime={currentTime} modalOpen={open} />
      </DialogContent>
      {canShareEvidence && (
        <ShareEventEvidenceDialog
          open={shareOpen}
          onOpenChange={setShareOpen}
          video={video}
          device={device}
        />
      )}
      {canViewForensic && hasEventCode && (
        <ForensicTelemetrySnapshotDialog
          open={forensicOpen}
          onOpenChange={setForensicOpen}
          video={video}
          device={device}
        />
      )}
    </Dialog>
  );
};

// MetaItem — icon + uppercase label + value, used by the Video Info Footer.
// Mirrors the same pattern on /evidence/<token> for visual parity.
const MetaItem = ({ icon: Icon, label, value, link, title, testid }) => (
  <div className="flex items-start gap-2" data-testid={testid}>
    <Icon className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" />
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      {link ? (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          title={title}
          className="text-[12px] text-blue-400 hover:underline break-words"
        >
          {value}
        </a>
      ) : (
        <div className="text-[12px] text-slate-200 break-words">{value}</div>
      )}
    </div>
  </div>
);

const formatEta = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return null;
  const mins = Math.floor(value / 60);
  const secs = Math.round(value % 60);
  return mins ? `${mins}m ${String(secs).padStart(2, '0')}s` : `${secs}s`;
};

const CompatibilityProgress = ({ progress, status }) => {
  if (!progress && !status) return null;
  const percent = Math.max(0, Math.min(100, Number(progress?.percent ?? 0)));
  const eta = formatEta(progress?.estimated_seconds_remaining);
  const inputMb = progress?.estimated_input_mb;
  const phaseLabel = {
    queued: 'Queued',
    starting: 'Starting',
    downloading: 'Downloading',
    video: 'Converting video',
    audio: 'Adding audio',
    uploading: 'Uploading',
    ready: 'Ready',
    failed: 'Failed',
  }[progress?.phase] || 'Preparing';
  return (
    <div className="mt-4 text-left" data-testid="video-compatibility-progress">
      <div className="flex items-center justify-between text-xs text-white/75 mb-1">
        <span data-testid="video-compatibility-progress-phase">{phaseLabel}</span>
        <span data-testid="video-compatibility-progress-percent">{Math.round(percent)}%</span>
      </div>
      <div className="h-2 bg-white/15 overflow-hidden rounded-sm" data-testid="video-compatibility-progress-bar">
        <div className="h-full bg-amber-400 transition-[width] duration-500" style={{ width: `${percent}%` }} />
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/55 mt-2">
        {eta && <span data-testid="video-compatibility-progress-eta">~{eta} remaining</span>}
        {inputMb && <span data-testid="video-compatibility-progress-size">Source {inputMb} MB</span>}
        {status && <span data-testid="video-compatibility-progress-status">{status}</span>}
      </div>
    </div>
  );
};
